<script setup lang="ts">
import { type FormInstance, type FormRules } from 'element-plus'
import { inject, reactive, ref } from 'vue'
import SelectAccount from '../SelectAccount.vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { LoginMobileAuth, LoginPreCheck, SendSMS } from '@/api/login'
import Captcha from '../Captcha/index.vue'
import { useCountDown, useIsSp } from '@mp/hooks'
import { IconFont } from '@mp/component'
import { captureEvent, captureMessage } from '@sentry/vue'

const toggle = inject('toggle', () => { })

const { company } = useIsSp()
const selectorRef = ref<InstanceType<typeof SelectAccount> | null>(null)

const captchaRef = ref<InstanceType<typeof Captcha> | null>(null)

const formRef = ref<FormInstance>()
const formMessage = reactive<{
  message: string | undefined
  type: 'default' | 'error' | 'success'
}>({
  message: undefined,
  type: 'default',
})

// #获取token
const { data: preCheckData, refetch: refetchPreCheckData, isLoading: isPreCheckDataLoading } = useQuery({
  queryKey: ['login:check'],
  queryFn: () => LoginPreCheck({
    needPwdWidget: false,
  }),
  refetchOnWindowFocus: false,
})

const rules = reactive<FormRules>({
  phone: [{ required: true, message: '请输入手机号', trigger: 'blur' }, { pattern: /^(?:(?:\+|00)86)?1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }],
  code: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
})

const formValues = reactive<{
  phone: string | undefined
  code: string | undefined
}>({
  phone: undefined,
  code: undefined,
})

const { time, start, isActive: cutdownIsActive } = useCountDown({
  default: 60,
})
// 发送验证码
const { mutateAsync: mutateSendSMS, data: smsmData, isLoading: isSendLoading } = useMutation({
  mutationFn: SendSMS,
  onSuccess() {
    start() // 开始倒计时
    ElMessage.success('验证码已发送')
  },
  onError: (e: any) => {
    const { errorCodeDes } = e
    if (errorCodeDes) {
      formMessage.message = errorCodeDes
      formMessage.type = 'error'
    }

    refetchPreCheckData()
  },
})

async function handleSend() {
  formRef.value?.validateField('phone', (validate: boolean, e: any) => {
    const lastError = e && [].concat(...Object.keys(e).map(key => e[key]))?.[0]
    if (lastError) {
      formMessage.message = lastError.message
      formMessage.type = 'error'
    }
    if (!validate)
      return
    if (preCheckData?.value?.needCaptcha) {
      captchaRef.value?.open()
      return
    }

    formMessage.message = undefined
    mutateSendSMS({
      mobileNo: formValues.phone!,
      tempToken: preCheckData.value?.tempToken as string,
    }).catch(() => undefined)
  })
}

// ?行为验证成功
function handleCaptchOnSuccess(value: string) {
  if (!preCheckData?.value?.tempToken)
    return

  mutateSendSMS({
    mobileNo: formValues.phone!,
    tempToken: preCheckData?.value?.tempToken,
    thirdCaptchaId: value,
  }).catch(() => undefined)
}

// 点击登录
const { mutateAsync, isLoading: isSubmitLoading } = useMutation({
  mutationFn: LoginMobileAuth,
  onSuccess(data) {
    if (!data.loginAwaitSelectors)
      return

    captureEvent({
      level: 'log',
      event_id: 'click_login',
      message: '点击登录',
      extra: {
        type: '使用短信登录',
      },
    })
    captureMessage('使用短信登录')

    // ?如果是单账号
    if (data?.loginAwaitSelectors?.length === 1) {
      selectorRef.value?.handleChecked(data?.loginAwaitSelectors[0], data.loginTicket)
      return
    }

    selectorRef.value?.open({
      data: data.loginAwaitSelectors,
      loginTicket: data.loginTicket,
    })
  },
  onError(e: any) {
    const { errorCodeDes } = e
    refetchPreCheckData()
    formMessage.message = undefined
    if (errorCodeDes) {
      formMessage.message = errorCodeDes
      formMessage.type = 'error'
    }
  },
})

function handleSubmit() {
  formRef.value?.validate((valid: boolean, fields: any) => {
    if (valid) {
      // ?是否发送过短信验证码
      if (!smsmData?.value?.smsVerifyTicket) {
        formMessage.message = '请先获取短信验证码'
        formMessage.type = 'error'
        return
      }

      formMessage.message = undefined

      if (!formValues.code)
        return
      mutateAsync({
        smsVerifyTicket: smsmData?.value?.smsVerifyTicket,
        smsVerifyCode: formValues.code,
      }).catch(() => undefined)
    }
    else {
      const lastError = [].concat(...Object.keys(fields).map(key => fields[key]))?.[0] as any
      if (lastError)
        formMessage.message = lastError.message
      formMessage.type = 'error'
    }
  })
}
</script>

<template>
  <h3 class="text-sm mb-[28px] mt-0 font-bold  leading-none">
    手机验证码登录
  </h3>

  <el-form ref="formRef" :rules="rules" :model="formValues" size="large" :show-message="false">
    <div class="h-[136px]">
      <el-form-item prop="phone" style="margin-bottom: 10px;">
        <el-input v-model="formValues.phone" placeholder="请输入手机号" max-length="11">
          <template #prefix>
            <div class="icon-container">
              <IconFont type="icon-iPhone" class="text-[#ccc] mr-2 text-4" />
            </div>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item prop="code" style="margin-bottom: 10px;">
        <el-input v-model="formValues.code" placeholder="请输入验证码">
          <template #prefix>
            <div class="icon-container">
              <IconFont type="icon-protect" class="text-[#ccc] mr-2 text-4" />
            </div>
          </template>

          <template #suffix>
            <el-button
              link type="primary" size="small" :loading="isSendLoading" :disabled="cutdownIsActive"
              @click="handleSend"
            >
              <template #loading>
                <IconFont type="icon-loading" class="text-[#ccc] mr-2 text-[20px]" />
              </template>
              {{ cutdownIsActive ? `${time}s` : '发送验证码' }}
            </el-button>
          </template>
        </el-input>
      </el-form-item>

      <div class="flex justify-between text-xs">
        <span
          v-if="formMessage.message" class="flex  items-start" :class="{
            'text-[var(--el-color-danger)]': formMessage.type === 'error',
            'text-[rgba(255,255,255,.7)]': formMessage.type === 'default',
          }"
        >
          <IconFont type="icon-exclamation-circle" class="text-base leading-none mr-1 -mt-[1px]" /> {{
            formMessage.message }}
        </span>
      </div>
    </div>

    <el-button type="primary" class="w-full mt-[24px]" size="large" :loading="isSubmitLoading || isPreCheckDataLoading" @click="handleSubmit">
      登录
    </el-button>
  </el-form>

  <div class=" flex justify-between mt-2 leading-none" style="--el-color-primary: #fff">
    <el-popover
      placement="bottom" :width="200" trigger="hover"
      :content="`建议您使用账号密码登录，或联系您的商户管理员修改联系人手机号。商户管理员联系手机无法使用请联系${company}客服`"
    >
      <template #reference>
        <el-button link type="primary" size="small" class="hover:underline text-#fff">
          收不到验证码?
        </el-button>
      </template>
    </el-popover>
    <p v-track class="hover:text-primary cursor-pointer flex items-center text-xs text-[#fff]" @click="toggle()">
      <IconFont type="icon-user" class=" text-lg mr-1  leading-none -mt-[2px]" /> 账号密码登录
    </p>
  </div>

  <Captcha ref="captchaRef" @success="handleCaptchOnSuccess" />
  <SelectAccount ref="selectorRef" type="sms" />
</template>

<style scoped>
.icon-container {
  @apply b-r b-r-solid b-r-[#eee] h-[20px] -ml-1 leading-none;
}
</style>
